import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ShoppingBag,
  Plane,
  Package,
  MessageCircle,
  LogOut,
  AlertTriangle,
  User,
  X,
  CheckCircle2,
  Bell,
  ShieldAlert,
} from "lucide-react";
import { supabase, configured } from "./lib/supabase";
import useNotifications from "./hooks/useNotifications.js";
import useMarketplaceData from "./hooks/useMarketplaceData.js";
import {
  getCashHandoff,
  scheduleCashHandoff,
  confirmCashReceived,
  subscribeToCashHandoff,
} from "./lib/cashHandoff";
import Landing from "./Landing.jsx";
import SiteFooter from "./components/SiteFooter.jsx";
import MarketplaceListings from "./components/MarketplaceListings.jsx";
import OrdersSection from "./components/OrdersSection.jsx";
import NotificationInbox from "./components/NotificationInbox.jsx";
import CashScheduleDialog from "./components/CashScheduleDialog.jsx";
import DisputeDialog from "./components/DisputeDialog.jsx";
import ProfileSection from "./components/ProfileSection.jsx";
import MarketplaceActionDialog from "./components/MarketplaceActionDialog.jsx";
const ChatSection = lazy(() => import("./components/ChatSection.jsx"));
import {
  getSafeInquiryReturnTo,
  isPublicProfilePath,
  resolveAppRoute,
} from "./lib/routes.js";
const PublicProfilePage = lazy(() =>
  import("./components/PublicProfiles.jsx").then((module) => ({
    default: module.PublicProfilePage,
  }))
);
const TripInquiriesPanel = lazy(() => import("./components/TripInquiries.jsx"));
const ProfileReputationSection = lazy(
  () => import("./components/ProfileReputationSection.jsx")
);
import {
  currentManilaDateTimeInput,
  manilaDateTimeToUtc,
  toManilaDateTimeInput,
} from "./lib/manilaTime.js";
import { getTransactionProfileCompletionMessage } from "./lib/profileCompletion.js";
import {
  getMarketplaceToday,
  isMarketplaceDateCurrent,
} from "./lib/marketplaceExpiration.js";
import {
  filterOffers,
  filterOrders,
  filterTrips,
} from "./lib/marketplaceFilters.js";
import {
  travelerOrderAction,
} from "./lib/orderPresentation.js";
import { listMyDisputes, openOrderDispute } from "./lib/disputes.js";
import {
  getMyReviewReports,
  getProfileReputations,
  getReviewsForOrders,
  getReviewsForProfile,
} from "./lib/reviews.js";
import TransactionReviewsSection from "./components/TransactionReviewsSection.jsx";
const AdminTravelerRoleRequestsPanel = lazy(
  () => import("./components/AdminTravelerRoleRequestsPanel.jsx")
);
const AdminDisputeReviewPanel = lazy(
  () => import("./components/AdminDisputeReviewPanel.jsx")
);
const AdminNoShowReportsPanel = lazy(
  () => import("./components/AdminNoShowReportsPanel.jsx")
);
const AdminReviewReportsPanel = lazy(
  () => import("./components/AdminReviewReportsPanel.jsx")
);
import "./marketplace-filters.css";
import "./notification-inbox.css";
import { DISPUTES_ENABLED } from "./lib/features.js";

const demoTrips = [
  {
    id: "demo-1",
    owner_id: "u2",
    origin: "Tokyo, Japan",
    destination: "Paranaque",
    travel_date: "2026-10-22",
    slots: 4,
    service_fee: 250,
    tags: ["Uniqlo", "Skincare"],
  },
  {
    id: "demo-2",
    owner_id: "u2",
    origin: "Baguio City",
    destination: "Metro Manila",
    travel_date: "2026-10-18",
    slots: 7,
    service_fee: 120,
    tags: ["Ube Jam", "Coffee"],
  },
];
const statusLabels = [
  "requested",
  "accepted",
  "purchased",
  "in_transit",
  "delivered",
];
export default function App() {
  const [path, setPath] = useState(window.location.pathname),
    [sessionReady, setSessionReady] = useState(false),
    [session, setSession] = useState(null),
    [profile, setProfile] = useState(null),
    [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [authMode, setAuthMode] = useState(() =>
      new URLSearchParams(window.location.search).get("mode") === "signup"
        ? "signup"
        : "signin"
    ),
    [recovery, setRecovery] = useState(false),
    [marketSearch, setMarketSearch] = useState(""),
    [marketDate, setMarketDate] = useState(""),
    [marketplaceToday, setMarketplaceToday] = useState(getMarketplaceToday),
    [orderSearch, setOrderSearch] = useState(""),
    [orderStatus, setOrderStatus] = useState(""),
    [notifications, setNotifications] = useState([]),
    [unreadNotificationCount, setUnreadNotificationCount] = useState(0),
    [notificationsHasMore, setNotificationsHasMore] = useState(false),
    [notificationsLoading, setNotificationsLoading] = useState(false),
    [notificationsOlderLoading, setNotificationsOlderLoading] = useState(false),
    [notificationsError, setNotificationsError] = useState(""),
    [notificationsOlderError, setNotificationsOlderError] = useState(""),
    [notificationBusyId, setNotificationBusyId] = useState(null),
    [notificationBulkBusy, setNotificationBulkBusy] = useState(false),
    [messages, setMessages] = useState([]),
    [messagesLoading, setMessagesLoading] = useState(false),
    [messageError, setMessageError] = useState(""),
    [sendingMessage, setSendingMessage] = useState(false),
    [proofUploading, setProofUploading] = useState(false),
    [proofError, setProofError] = useState(""),
    [proofNotice, setProofNotice] = useState(""),
    [tab, setTab] = useState("market"),
    [notice, setNotice] = useState(""),
    [modal, setModal] = useState(null),
    [selectedTrip, setSelectedTrip] = useState(null),
    [selectedOffer, setSelectedOffer] = useState(null),
    [selectedOrder, setSelectedOrder] = useState(null),
    [disputes, setDisputes] = useState({}),
    [disputesLoaded, setDisputesLoaded] = useState(false),
    [disputesError, setDisputesError] = useState(""),
    [disputeOrder, setDisputeOrder] = useState(null),
    [disputeReason, setDisputeReason] = useState(""),
    [disputeBusy, setDisputeBusy] = useState(false),
    [disputeError, setDisputeError] = useState(""),
    [dialogErrors, setDialogErrors] = useState({}),
    [draft, setDraft] = useState(""),
    [request, setRequest] = useState({
      item: "",
      budget: "",
      city: "Paranaque",
    }),
    [newTrip, setNewTrip] = useState({
      origin: "",
      destination: "",
      date: "",
      slots: "3",
      fee: "150",
      tags: "",
      description: "",
    }),
    [newErrand, setNewErrand] = useState({
      area: "",
      store: "",
      date: "",
      fee: "100",
      slots: "3",
      description: "",
    }),
    [newPassword, setNewPassword] = useState(""),
    [cashHandoff, setCashHandoff] = useState(null),
    [cashModal, setCashModal] = useState(false),
    [cashForm, setCashForm] = useState({ place: "", time: "", landmark: "" }),
    [inspection, setInspection] = useState({
      item: false,
      quantity: false,
      condition: false,
      amount: false,
    }),
    [cashBusy, setCashBusy] = useState(false);
  const [reputations, setReputations] = useState({}),
    [reputationError, setReputationError] = useState(""),
    [reputationRefresh, setReputationRefresh] = useState(0),
    [orderReviewData, setOrderReviewData] = useState({}),
    [orderReviewLoading, setOrderReviewLoading] = useState(false),
    [orderReviewError, setOrderReviewError] = useState(""),
    [viewedProfileId, setViewedProfileId] = useState(null),
    [reviewProfile, setReviewProfile] = useState(null),
    [profileReviews, setProfileReviews] = useState([]),
    [profileReviewAuthors, setProfileReviewAuthors] = useState({}),
    [profileReviewsLoading, setProfileReviewsLoading] = useState(false),
    [profileReviewsError, setProfileReviewsError] = useState(""),
    [ownerProfiles, setOwnerProfiles] = useState({}),
    [ownerProfilesError, setOwnerProfilesError] = useState("");
  const [orderBuyerProfiles, setOrderBuyerProfiles] = useState({});
  const [orderBuyerProfilesLoading, setOrderBuyerProfilesLoading] =
    useState(false);
  const [orderBuyerProfilesError, setOrderBuyerProfilesError] = useState("");
  const [orderTravelerProfiles, setOrderTravelerProfiles] = useState({});
  const [orderTravelerProfilesLoading, setOrderTravelerProfilesLoading] =
    useState(false);
  const [orderTravelerProfilesError, setOrderTravelerProfilesError] = useState("");
  const [profileReviewReports, setProfileReviewReports] = useState({}),
    [profileReviewReportsLoading, setProfileReviewReportsLoading] =
      useState(false),
    [profileReviewReportsError, setProfileReviewReportsError] = useState("");
  const [orderReviewRefresh, setOrderReviewRefresh] = useState(0),
    [profileReviewRefresh, setProfileReviewRefresh] = useState(0);
  const [marketProfileRefresh, setMarketProfileRefresh] = useState(0);
  const [authFeedback, setAuthFeedback] = useState(null),
    [authBusy, setAuthBusy] = useState(false);
  const authEmailRef = useRef(null);
  const [cashHandoffError, setCashHandoffError] = useState(""),
    [cashHandoffReload, setCashHandoffReload] = useState(0);
  const messageLoadRequest = useRef(0),
    marketplaceFiltersInitialized = useRef(false),
    disputesLoadRequest = useRef(0),
    dialogRef = useRef(null);
  const disputesLoadedFor = useRef(""),
    orderReviewsLoadedFor = useRef("");
  const uid = session?.user?.id;
  const role = profile?.role || "buyer";
  function alert(message) {
    setNotice(message);
    setTimeout(() => setNotice(""), 2600);
  }
  const {
    trips,
    tripsLoading,
    setTripsLoading,
    tripsError,
    offers,
    offersLoading,
    setOffersLoading,
    offersError,
    orders,
    ordersLoading,
    setOrdersLoading,
    ordersError,
    tripsHasMore,
    offersHasMore,
    ordersHasMore,
    tripsMoreLoading,
    offersMoreLoading,
    ordersMoreLoading,
    tripsMoreError,
    offersMoreError,
    ordersMoreError,
    marketplaceHasLoaded,
    reload,
    loadMoreMarketplace,
  } = useMarketplaceData({
    userId: uid,
    marketSearch,
    marketDate,
    orderSearch,
    orderStatus,
    initialTrips: supabase ? [] : demoTrips,
    onProfileLoaded: setProfile,
    onProfileLoadError: alert,
  });
  const route = resolveAppRoute(path, sessionReady, Boolean(session), recovery);
  function getProfileCompletionMessage(action) {
    if (!supabase || !uid || uid === "demo") return "";
    return getTransactionProfileCompletionMessage(profile, action);
  }
  function requireCompleteProfile(action) {
    const message = getProfileCompletionMessage(action);
    if (!message) return true;
    setModal(null);
    setCashModal(false);
    setTab("profile");
    alert(message);
    return false;
  }
  function openProfileSettings() {
    setTab("profile");
    navigate("/app", true);
  }
  const { loadNotifications, markNotificationRead, markAllNotificationsRead } =
    useNotifications({
      userId: uid,
      tab,
      notifications,
      notificationsHasMore,
      notificationsOlderLoading,
      notificationBusyId,
      notificationBulkBusy,
      unreadNotificationCount,
      setNotifications,
      setUnreadNotificationCount,
      setNotificationsHasMore,
      setNotificationsLoading,
      setNotificationsOlderLoading,
      setNotificationsError,
      setNotificationsOlderError,
      setNotificationBusyId,
      setNotificationBulkBusy,
    });
  const filteredTrips = useMemo(
    () =>
      filterTrips(
        trips.filter((trip) =>
          isMarketplaceDateCurrent(trip.travel_date, marketplaceToday)
        ),
        { query: marketSearch, date: marketDate }
      ),
    [trips, marketSearch, marketDate, marketplaceToday]
  );
  const filteredOffers = useMemo(
    () =>
      filterOffers(
        offers.filter((offer) =>
          isMarketplaceDateCurrent(offer.planned_date, marketplaceToday)
        ),
        {
          query: marketSearch,
          date: marketDate,
          buyerCity: profile?.public_city || profile?.city,
          buyerBarangay:
            profile?.public_barangay_code ||
            profile?.barangay_code ||
            profile?.public_barangay ||
            profile?.barangay,
          ownerProfiles,
          role,
        }
      ),
    [offers, marketSearch, marketDate, marketplaceToday, profile, ownerProfiles, role]
  );
  const filteredOrders = useMemo(
    () => filterOrders(orders, { query: orderSearch, status: orderStatus }),
    [orders, orderSearch, orderStatus]
  );
  const hasMarketplaceFilter = Boolean(marketSearch.trim() || marketDate);
  const activeDialog = disputeOrder
    ? "dispute"
    : cashModal
    ? "cash"
    : modal
    ? "listing"
    : null;
  const dialogBusy = useRef({ dispute: false, cash: false });
  dialogBusy.current = { dispute: disputeBusy, cash: cashBusy };
  function clearMarketplaceFilters() {
    setMarketSearch("");
    setMarketDate("");
  }
  const hasOrderFilter = Boolean(orderSearch.trim() || orderStatus);
  function clearOrderFilters() {
    setOrderSearch("");
    setOrderStatus("");
  }
  const unreadNotificationLabel =
    unreadNotificationCount > 99 ? "99+" : String(unreadNotificationCount);
  function navigate(path, replace = false) {
    if (replace) window.history.replaceState({}, "", path);
    else window.history.pushState({}, "", path);
    setPath(path.split("?")[0]);
  }
  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  function validateDialogForm(event, trimFields = []) {
    event.preventDefault();
    const form = event.currentTarget,
      errors = {};
    for (const field of form.elements) {
      if (!field.name) continue;
      const value = String(field.value || "");
      if (trimFields.includes(field.name) && !value.trim())
        errors[field.name] = value
          ? "Enter a value without only spaces."
          : "This field is required.";
      else if (!field.checkValidity())
        errors[field.name] = field.validationMessage || "Check this value.";
    }
    setDialogErrors(errors);
    const firstInvalid = Object.keys(errors)[0];
    if (firstInvalid)
      requestAnimationFrame(() =>
        form.elements.namedItem(firstInvalid)?.focus()
      );
    return Object.keys(errors).length === 0;
  }
  function validateDisputeForm(event) {
    event.preventDefault();
    const form = event.currentTarget,
      length = disputeReason.trim().length;
    const error =
      length < 10
        ? "Describe the issue in at least 10 non-space characters."
        : length > 2000
        ? "Keep the issue description to 2000 characters or fewer."
        : "";
    setDialogErrors(error ? { disputeReason: error } : {});
    if (error) {
      requestAnimationFrame(() =>
        form.elements.namedItem("disputeReason")?.focus()
      );
      return;
    }
    submitDispute();
  }
  function clearDialogError(name) {
    setDialogErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  }
  function updateListingField(type, field, value) {
    clearDialogError(field);
    if (type === "trip") {
      setNewTrip((current) => ({ ...current, [field]: value }));
    } else {
      setNewErrand((current) => ({ ...current, [field]: value }));
    }
  }
  function updateRequestField(field, value) {
    clearDialogError(field);
    setRequest((current) => ({ ...current, [field]: value }));
  }
  async function loadDisputes() {
    if (!DISPUTES_ENABLED) {
      setDisputes({});
      setDisputesLoaded(true);
      setDisputesError("");
      disputesLoadedFor.current = uid || "";
      return;
    }
    const requestId = ++disputesLoadRequest.current;
    setDisputesLoaded(false);
    if (!supabase || !uid) {
      setDisputes({});
      setDisputesError("");
      disputesLoadedFor.current = "";
      return;
    }
    setDisputes({});
    setDisputesError("");
    try {
      const { data, error } = await listMyDisputes();
      if (error) throw error;
      if (requestId !== disputesLoadRequest.current) return;
      setDisputes(
        Object.fromEntries((data || []).map((item) => [item.order_id, item]))
      );
      setDisputesLoaded(true);
      disputesLoadedFor.current = uid;
    } catch (error) {
      if (requestId === disputesLoadRequest.current)
        setDisputesError(
          `Could not load issue reports: ${
            error.message || "Network request failed."
          }`
        );
    }
  }
  async function submitDispute() {
    const reason = disputeReason.trim();
    if (!disputeOrder) return;
    if (reason.length < 10 || reason.length > 2000) {
      setDialogErrors({
        disputeReason:
          reason.length < 10
            ? "Describe the issue in at least 10 non-space characters."
            : "Keep the issue description to 2000 characters or fewer.",
      });
      requestAnimationFrame(() =>
        document.getElementById("dispute-reason")?.focus()
      );
      return;
    }
    setDisputeBusy(true);
    setDisputeError("");
    try {
      const { error } = await openOrderDispute(disputeOrder.id, reason);
      if (error) throw error;
      setDisputeOrder(null);
      setDisputeReason("");
      alert("Issue report submitted. The order is now marked disputed.");
      await Promise.all([reload(), loadDisputes()]);
    } catch (error) {
      setDisputeError(
        `Could not submit issue report: ${
          error.message || "Network request failed."
        }`
      );
    } finally {
      setDisputeBusy(false);
    }
  }
  useEffect(() => {
    if (!supabase) {
      setSessionReady(true);
      return;
    }
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session);
        setSessionReady(true);
      }
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setSessionReady(true);
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);
  useEffect(() => {
    if (!sessionReady) return;
    if (path === "/app" && !session) navigate("/auth", true);
    else if (path === "/auth" && session && !recovery) {
      let savedReturn = "";
      try {
        savedReturn =
          window.sessionStorage.getItem("sabaybuy-trip-inquiry-return") || "";
        window.sessionStorage.removeItem("sabaybuy-trip-inquiry-return");
      } catch {
        setNotice(
          "Your browser could not restore the trip inquiry link. Reopen the public profile after signing in."
        );
      }
      const inquiryReturn = getSafeInquiryReturnTo(savedReturn);
      if (inquiryReturn) navigate(inquiryReturn, true);
      else navigate("/app", true);
    } else if (
      !["/", "/auth", "/app"].includes(path) &&
      !isPublicProfilePath(path)
    )
      navigate("/", true);
  }, [path, session, sessionReady, recovery]);
  useEffect(() => {
    reload();
  }, [uid]);
  useEffect(() => {
    if (!uid) {
      setDisputes({});
      setDisputesLoaded(false);
      setDisputesError("");
      disputesLoadedFor.current = "";
      return;
    }
    if (tab !== "orders" || disputesLoadedFor.current === uid) return;
    loadDisputes();
    return () => {
      disputesLoadRequest.current += 1;
    };
  }, [uid, tab]);
  useEffect(() => {
    let active = true;
    const ids = [
      ...new Set(
        [
          uid,
          ...trips.map((item) => item.owner_id),
          ...offers.map((item) => item.owner_id),
          viewedProfileId,
        ].filter(Boolean)
      ),
    ];
    if (!supabase || !uid || ids.length === 0) {
      setReputations({});
      setReputationError("");
      return () => {
        active = false;
      };
    }
    getProfileReputations(ids)
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          setReputationError(error.message || "Could not load reputation.");
          return;
        }
        setReputationError("");
        setReputations(
          Object.fromEntries(
            (data || []).map((item) => [item.profile_id, item])
          )
        );
      })
      .catch((error) => {
        if (active)
          setReputationError(error.message || "Could not load reputation.");
      });
    return () => {
      active = false;
    };
  }, [trips, offers, uid, viewedProfileId, reputationRefresh]);
  useEffect(() => {
    let active = true;
    const ids = [
      ...new Set(
        [
          ...trips.map((item) => item.owner_id),
          ...offers.map((item) => item.owner_id),
        ].filter(Boolean)
      ),
    ];
    if (!supabase || !uid || ids.length === 0) {
      setOwnerProfiles({});
      setOwnerProfilesError("");
      return () => {
        active = false;
      };
    }
    setOwnerProfilesError("");
    supabase
      .from("profiles")
      .select(
        "id,display_name,role,verified,public_profile_id,is_public,public_avatar_path,public_city,public_barangay_code"
      )
      .in("id", ids)
      .then(async ({ data, error }) => {
        if (!active) return;
        if (error) {
          setOwnerProfiles({});
          setOwnerProfilesError(
            error.message || "Could not load traveler review status."
          );
          return;
        }
        const profiles = await Promise.all(
          (data || []).map(async (item) => {
            if (!item.is_public || !item.public_avatar_path)
              return [item.id, { ...item, photoUrl: "", photoError: "" }];
            const { data: photoData, error: photoError } = await supabase.storage
              .from("public-profile-photos")
              .createSignedUrl(item.public_avatar_path, 300);
            return [
              item.id,
              {
                ...item,
                photoUrl: photoError ? "" : photoData?.signedUrl || "",
                photoError: photoError?.message || "",
              },
            ];
          })
        );
        if (active) setOwnerProfiles(Object.fromEntries(profiles));
      })
      .catch((error) => {
        if (active) {
          setOwnerProfiles({});
          setOwnerProfilesError(
            error.message || "Could not load traveler review status."
          );
        }
      });
    return () => {
      active = false;
    };
  }, [trips, offers, uid, marketProfileRefresh]);
  useEffect(() => {
    let active = true;
    const buyerIds =
      role === "traveler"
        ? [...new Set(orders.map((order) => order.buyer_id).filter(Boolean))]
        : [];
    if (!supabase || !uid || buyerIds.length === 0) {
      setOrderBuyerProfiles({});
      setOrderBuyerProfilesLoading(false);
      setOrderBuyerProfilesError("");
      return () => {
        active = false;
      };
    }

    setOrderBuyerProfiles({});
    setOrderBuyerProfilesLoading(true);
    setOrderBuyerProfilesError("");
    const privatePhotoUrls = [];
    async function loadBuyerProfiles() {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id,display_name,public_profile_id,is_public,public_avatar_path")
          .in("id", buyerIds);
        if (error) throw error;
        const profiles = await Promise.all(
          (data || []).map(async (buyer) => {
            if (!buyer.public_avatar_path)
              return [buyer.id, { ...buyer, photoUrl: "", photoError: "" }];
            const photoResult = buyer.is_public
              ? await supabase.storage
                  .from("public-profile-photos")
                  .createSignedUrl(buyer.public_avatar_path, 300)
              : await supabase.storage
                  .from("public-profile-photos")
                  .download(buyer.public_avatar_path);
            const photoError = photoResult.error;
            if (photoError) {
              return [
                buyer.id,
                { ...buyer, photoUrl: "", photoError: photoError.message },
              ];
            }
            const photoUrl = buyer.is_public
              ? photoResult.data?.signedUrl || ""
              : URL.createObjectURL(photoResult.data);
            if (!buyer.is_public && photoUrl) privatePhotoUrls.push(photoUrl);
            return [
              buyer.id,
              { ...buyer, photoUrl, photoError: "" },
            ];
          })
        );
        if (active) setOrderBuyerProfiles(Object.fromEntries(profiles));
        else privatePhotoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
      } catch (error) {
        if (active)
          setOrderBuyerProfilesError(
            error.message || "Could not load buyer profile details."
          );
      } finally {
        if (active) setOrderBuyerProfilesLoading(false);
      }
    }
    loadBuyerProfiles();
    return () => {
      active = false;
      privatePhotoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
    };
  }, [orders, role, uid]);

  useEffect(() => {
    let active = true;
    const travelerIds =
      role === "buyer"
        ? [...new Set(orders.map((order) => order.traveler_id).filter(Boolean))]
        : [];
    if (!supabase || !uid || travelerIds.length === 0) {
      setOrderTravelerProfiles({});
      setOrderTravelerProfilesLoading(false);
      setOrderTravelerProfilesError("");
      return () => {
        active = false;
      };
    }

    setOrderTravelerProfiles({});
    setOrderTravelerProfilesLoading(true);
    setOrderTravelerProfilesError("");
    const privatePhotoUrls = [];
    async function loadTravelerProfiles() {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id,display_name,public_profile_id,is_public,public_avatar_path")
          .in("id", travelerIds);
        if (error) throw error;
        const profiles = await Promise.all(
          (data || []).map(async (traveler) => {
            if (!traveler.public_avatar_path)
              return [traveler.id, { ...traveler, photoUrl: "", photoError: "" }];
            const photoResult = traveler.is_public
              ? await supabase.storage
                  .from("public-profile-photos")
                  .createSignedUrl(traveler.public_avatar_path, 300)
              : await supabase.storage
                  .from("public-profile-photos")
                  .download(traveler.public_avatar_path);
            const photoError = photoResult.error;
            if (photoError) {
              return [
                traveler.id,
                { ...traveler, photoUrl: "", photoError: photoError.message },
              ];
            }
            const photoUrl = traveler.is_public
              ? photoResult.data?.signedUrl || ""
              : URL.createObjectURL(photoResult.data);
            if (!traveler.is_public && photoUrl) privatePhotoUrls.push(photoUrl);
            return [
              traveler.id,
              { ...traveler, photoUrl, photoError: "" },
            ];
          })
        );
        if (active) setOrderTravelerProfiles(Object.fromEntries(profiles));
        else privatePhotoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
      } catch (error) {
        if (active)
          setOrderTravelerProfilesError(
            error.message || "Could not load traveler profile details."
          );
      } finally {
        if (active) setOrderTravelerProfilesLoading(false);
      }
    }
    loadTravelerProfiles();
    return () => {
      active = false;
      privatePhotoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
    };
  }, [orders, role, uid]);
  useEffect(() => {
    orderReviewsLoadedFor.current = "";
  }, [tab, uid]);
  useEffect(() => {
    if (tab !== "orders") return;
    let active = true;
    const ids = orders.map((order) => order.id);
    if (!supabase || !uid || ids.length === 0) {
      setOrderReviewData({});
      setOrderReviewError("");
      setOrderReviewLoading(false);
      return () => {
        active = false;
      };
    }
    const requestKey = `${uid}:${orders
      .map((order) => `${order.id}:${order.status}:${order.payment_state}`)
      .join(",")}:${orderReviewRefresh}`;
    if (orderReviewsLoadedFor.current === requestKey) return;
    setOrderReviewLoading(true);
    setOrderReviewData({});
    setOrderReviewError("");
    getReviewsForOrders(ids)
      .then(async ({ handoffs, reviews, error }) => {
        if (!active) return;
        if (error) throw error;
        const reportResult = await getMyReviewReports(
          reviews.map((review) => review.id)
        );
        if (!active) return;
        const reports = reportResult.data || [];
        const next = {};
        for (const id of ids)
          next[id] = {
            handoffStatus:
              handoffs.find((handoff) => handoff.order_id === id)?.status ||
              null,
            reviews: reviews.filter((review) => review.order_id === id),
            reviewReports: reports.filter((report) =>
              reviews.some(
                (review) =>
                  review.order_id === id && review.id === report.review_id
              )
            ),
            reviewReportsError: reportResult.error?.message || "",
          };
        setOrderReviewData(next);
        setOrderReviewError("");
        orderReviewsLoadedFor.current = requestKey;
      })
      .catch((error) => {
        if (active) {
          setOrderReviewError(
            error.message || "Could not load transaction reviews."
          );
          setOrderReviewData({});
        }
      })
      .finally(() => {
        if (active) setOrderReviewLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orders, uid, orderReviewRefresh, tab]);
  useEffect(() => {
    if (tab !== "profile") return;
    let active = true;
    const profileId = viewedProfileId || uid;
    if (!supabase || !profileId) {
      setReviewProfile(profile || null);
      setProfileReviews([]);
      setProfileReviewsError("");
      setProfileReviewReports({});
      setProfileReviewReportsError("");
      return () => {
        active = false;
      };
    }
    setProfileReviewsLoading(true);
    setProfileReviewReportsLoading(true);
    setProfileReviewReports({});
    setProfileReviewReportsError("");
    Promise.all([
      supabase
        .from("profiles")
        .select("id,display_name,role,verified")
        .eq("id", profileId)
        .maybeSingle(),
      getReviewsForProfile(profileId),
    ])
      .then(async ([personResult, reviewsResult]) => {
        if (!active) return;
        if (personResult.error || reviewsResult.error) {
          setProfileReviewsError(
            personResult.error?.message ||
              reviewsResult.error?.message ||
              "Could not load profile reviews."
          );
          return;
        }
        const received = reviewsResult.data || [],
          authorIds = [
            ...new Set(
              received
                .map((review) => review.reviewer_id)
                .filter((id) => id !== profileId)
            ),
          ];
        let authorMap = {};
        if (authorIds.length) {
          const { data, error } = await supabase
            .from("profiles")
            .select("id,display_name")
            .in("id", authorIds);
          if (!active) return;
          if (error) {
            setProfileReviewsError(
              error.message || "Could not load review authors."
            );
            return;
          }
          authorMap = Object.fromEntries(
            (data || []).map((author) => [author.id, author.display_name])
          );
        }
        const reportResult = await getMyReviewReports(
          received.map((review) => review.id)
        );
        if (!active) return;
        setProfileReviewReports(
          Object.fromEntries(
            (reportResult.data || []).map((report) => [
              report.review_id,
              report,
            ])
          )
        );
        setProfileReviewReportsError(reportResult.error?.message || "");
        setReviewProfile(personResult.data || null);
        setProfileReviews(received);
        setProfileReviewAuthors(authorMap);
        setProfileReviewsError("");
      })
      .catch((error) => {
        if (active)
          setProfileReviewsError(
            error.message || "Could not load profile reviews."
          );
      })
      .finally(() => {
        if (active) {
          setProfileReviewsLoading(false);
          setProfileReviewReportsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [tab, viewedProfileId, uid, profileReviewRefresh]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      const today = getMarketplaceToday();
      setMarketplaceToday((current) => (current === today ? current : today));
    }, 60000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!marketplaceFiltersInitialized.current) {
      marketplaceFiltersInitialized.current = true;
      return;
    }
    if (!uid) return;
    setTripsLoading(true);
    setOffersLoading(true);
    setOrdersLoading(true);
    const timer = setTimeout(() => reload(), 300);
    return () => clearTimeout(timer);
  }, [marketSearch, marketDate, orderSearch, orderStatus, marketplaceToday]);
  useEffect(() => {
    if (!supabase || !uid) return;
    const ch = supabase
      .channel("orders-live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders" },
        reload
      )
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [uid]);
  useEffect(() => {
    if (!activeDialog) {
      setDialogErrors({});
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog) return;
    const returnFocus = document.activeElement;
    dialog.focus();
    function closeDialog() {
      if (activeDialog === "dispute") {
        if (!dialogBusy.current.dispute) setDisputeOrder(null);
      } else if (activeDialog === "cash") {
        if (!dialogBusy.current.cash) setCashModal(false);
      } else setModal(null);
    }
    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [
        ...dialog.querySelectorAll(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
        ),
      ].filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first ||
          document.activeElement === dialog ||
          !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (returnFocus instanceof HTMLElement && returnFocus.isConnected)
        returnFocus.focus();
    };
  }, [activeDialog]);
  useEffect(() => {
    if (!supabase || !selectedOrder) {
      setMessages([]);
      setMessagesLoading(false);
      setMessageError("");
      return;
    }
    loadMessages(selectedOrder.id);
    const ch = supabase
      .channel("messages-" + selectedOrder.id)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `order_id=eq.${selectedOrder.id}`,
        },
        (p) =>
          setMessages((m) =>
            m.some((x) => x.id === p.new.id) ? m : [...m, p.new]
          )
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT")
          setMessageError(
            "Live chat updates are unavailable. You can still retry loading messages."
          );
      });
    return () => {
      messageLoadRequest.current += 1;
      supabase.removeChannel(ch);
    };
  }, [selectedOrder?.id]);
  async function auth(e) {
    e.preventDefault();
    setAuthFeedback(null);
    setAuthBusy(true);
    try {
      if (!supabase) {
        setSession({ user: { id: "demo", email: "demo@sabaybuy.ph" } });
        setProfile({ id: "demo", display_name: "Demo User", role: "buyer" });
        navigate("/app", true);
        return;
      }
      const result =
        authMode === "signup"
          ? await supabase.auth.signUp({
              email,
              password,
              options: {
                data: { display_name: email.split("@")[0] },
                emailRedirectTo: `${window.location.origin}/auth`,
              },
            })
          : await supabase.auth.signInWithPassword({ email, password });
      if (result.error) {
        setAuthFeedback({ type: "error", text: result.error.message });
        return;
      }
      setAuthFeedback({
        type: "success",
        text:
          authMode === "signup"
            ? "Check your email to confirm your account."
            : "Signed in successfully.",
      });
    } catch (error) {
      setAuthFeedback({
        type: "error",
        text:
          error.message ||
          "Authentication failed. Check your connection and try again.",
      });
    } finally {
      setAuthBusy(false);
    }
  }
  async function signInWithGoogle() {
    setAuthFeedback(null);
    if (!supabase) {
      setAuthFeedback({
        type: "error",
        text: "Google sign-in is unavailable in demo mode. Connect Supabase and configure the Google provider.",
      });
      return;
    }
    setAuthBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: `${window.location.origin}/auth` },
      });
      if (error) setAuthFeedback({ type: "error", text: error.message });
    } catch (error) {
      setAuthFeedback({
        type: "error",
        text:
          error.message ||
          "Could not start Google sign-in. Check your connection and try again.",
      });
    } finally {
      setAuthBusy(false);
    }
  }
  async function signInWithFacebook() {
    setAuthFeedback(null);
    if (!supabase) {
      setAuthFeedback({
        type: "error",
        text: "Facebook sign-in is unavailable in demo mode. Connect Supabase and configure the Facebook provider.",
      });
      return;
    }
    setAuthBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "facebook",
        options: { redirectTo: `${window.location.origin}/auth` },
      });
      if (error) setAuthFeedback({ type: "error", text: error.message });
    } catch (error) {
      setAuthFeedback({
        type: "error",
        text:
          error.message ||
          "Could not start Facebook sign-in. Check your connection and try again.",
      });
    } finally {
      setAuthBusy(false);
    }
  }
  async function forgot() {
    setAuthFeedback(null);
    if (!authEmailRef.current?.checkValidity()) {
      setAuthFeedback({
        type: "error",
        text: "Enter a valid email address to receive a password reset link.",
      });
      authEmailRef.current?.focus();
      return;
    }
    if (!supabase) {
      setAuthFeedback({
        type: "error",
        text: "Password recovery is unavailable in demo mode. Connect Supabase and try again.",
      });
      return;
    }
    setAuthBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      setAuthFeedback(
        error
          ? { type: "error", text: error.message }
          : {
              type: "success",
              text: "If an account exists for that email, a password reset link will be sent.",
            }
      );
    } catch (error) {
      setAuthFeedback({
        type: "error",
        text:
          error.message ||
          "Could not request a password reset. Check your connection and try again.",
      });
    } finally {
      setAuthBusy(false);
    }
  }
  async function updatePassword() {
    setAuthFeedback(null);
    if (!supabase) {
      setAuthFeedback({
        type: "error",
        text: "Password updates are unavailable in demo mode.",
      });
      return;
    }
    setAuthBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) {
        setAuthFeedback({ type: "error", text: error.message });
        return;
      }
      setRecovery(false);
      setNewPassword("");
      setAuthFeedback({
        type: "success",
        text: "Password updated successfully.",
      });
    } catch (error) {
      setAuthFeedback({
        type: "error",
        text:
          error.message ||
          "Could not update your password. Check your connection and try again.",
      });
    } finally {
      setAuthBusy(false);
    }
  }
  async function createOrder() {
    if (!supabase) return alert("Connect Supabase to create cloud orders");
    if (!requireCompleteProfile("requesting an item")) return;
    const budget = Number(request.budget);
    if (
      !request.item.trim() ||
      !Number.isFinite(budget) ||
      budget <= 0 ||
      !request.city.trim()
    )
      return alert(
        "Enter an item, a positive item budget, and a delivery city/area"
      );
    try {
      const { data, error: contactError } = await supabase
        .from("private_contact_details")
        .select("user_id")
        .eq("user_id", uid)
        .maybeSingle();
      if (contactError) throw contactError;
      if (!data) {
        setModal(null);
        setTab("profile");
        return alert(
          "Add your mobile number and Facebook profile link in Profile before requesting an item."
        );
      }
    } catch (contactError) {
      return alert(
        `Could not verify your contact details: ${
          contactError.message || "Network request failed."
        }`
      );
    }
    const isErrand = Boolean(selectedOffer);
    const { error } = await supabase
      .from("orders")
      .insert({
        buyer_id: uid,
        traveler_id: isErrand ? selectedOffer.owner_id : selectedTrip.owner_id,
        trip_id: isErrand ? null : selectedTrip.id,
        local_offer_id: isErrand ? selectedOffer.id : null,
        item_name: request.item.trim(),
        budget,
        delivery_city: request.city.trim(),
      });
    if (error) return alert(error.message);
    setModal(null);
    setTab("orders");
    await reload();
    alert("Request created");
  }
  async function createTrip() {
    if (!requireCompleteProfile("posting a trip")) return;
    if (
      !newTrip.origin.trim() ||
      !newTrip.destination.trim() ||
      !newTrip.date ||
      !Number.isFinite(Number(newTrip.slots)) ||
      Number(newTrip.slots) < 1 ||
      !Number.isFinite(Number(newTrip.fee)) ||
      Number(newTrip.fee) < 0
    )
      return alert("Complete the trip details with a valid slot count and fee");
    try {
      const { data, error: contactError } = await supabase
        .from("private_contact_details")
        .select("user_id")
        .eq("user_id", uid)
        .maybeSingle();
      if (contactError) throw contactError;
      if (!data) {
        setModal(null);
        setTab("profile");
        return alert(
          "Add your mobile number and Facebook profile link in Profile before posting a trip."
        );
      }
    } catch (contactError) {
      return alert(
        `Could not verify your contact details: ${
          contactError.message || "Network request failed."
        }`
      );
    }
    const { error } = await supabase.from("trips").insert({
      owner_id: uid,
      origin: newTrip.origin.trim(),
      destination: newTrip.destination.trim(),
      travel_date: newTrip.date,
      slots: Number(newTrip.slots),
      service_fee: Number(newTrip.fee),
      description: newTrip.description.trim(),
      tags: newTrip.tags
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean),
    });
    if (error) return alert(error.message);
    setModal(null);
    await reload();
    alert("Trip published");
  }
  async function deactivateTrip(trip) {
    if (!supabase) return alert("Connect Supabase to update cloud trips");
    if (trip.owner_id !== uid || !trip.active) return;
    if (
      !window.confirm(
        "Deactivate this trip? It will no longer accept new requests. Existing orders will remain unchanged."
      )
    )
      return;
    const { data, error } = await supabase
      .from("trips")
      .update({ active: false })
      .eq("id", trip.id)
      .eq("active", true)
      .select("id")
      .maybeSingle();
    if (error) return alert(`Could not deactivate trip: ${error.message}`);
    if (!data) {
      await reload();
      return alert("This trip is already inactive or could not be updated.");
    }
    await reload();
    alert("Trip deactivated. Existing orders are unchanged.");
  }
  async function createErrand() {
    if (!requireCompleteProfile("offering a local errand")) return;
    const fee = Number(newErrand.fee),
      slots = Number(newErrand.slots);
    if (
      !newErrand.area.trim() ||
      !newErrand.store.trim() ||
      !newErrand.date ||
      !Number.isFinite(fee) ||
      fee < 0 ||
      !Number.isInteger(slots) ||
      slots < 1
    )
      return alert(
        "Enter a local area, store, planned date, valid runner fee, and positive whole-number slot count"
      );
    const { error } = await supabase
      .from("local_errand_offers")
      .insert({
        owner_id: uid,
        public_area: newErrand.area.trim(),
        store_name: newErrand.store.trim(),
        planned_date: newErrand.date,
        runner_fee: fee,
        slots,
        description: newErrand.description.trim(),
      });
    if (error) return alert(error.message);
    setModal(null);
    await reload();
    alert("Local errand offer published");
  }
  async function deactivateOffer(offer) {
    const { error } = await supabase
      .from("local_errand_offers")
      .update({ active: false })
      .eq("id", offer.id);
    if (error) return alert(error.message);
    await reload();
    alert("Local errand offer deactivated");
  }
  async function patchOrder(o, patch) {
    const { error } = await supabase
      .from("orders")
      .update(patch)
      .eq("id", o.id);
    if (error) alert(error.message);
    else reload();
  }
  async function advanceOrder(order) {
    const action = travelerOrderAction(order.status);
    if (!supabase) return alert("Connect Supabase to update cloud orders");
    if (role !== "traveler" || order.traveler_id !== uid || !action) return;
    if (!requireCompleteProfile("updating this order")) return;
    try {
      const { data, error } = await supabase
        .from("orders")
        .update({ status: action.nextStatus })
        .eq("id", order.id)
        .eq("traveler_id", uid)
        .eq("status", order.status)
        .select("id")
        .maybeSingle();
      if (error) return alert(`Could not update order: ${error.message}`);
      if (!data) {
        await reload();
        return alert(
          "This order has changed and can no longer be updated from its current stage."
        );
      }
      await reload();
      alert(`${action.label} completed.`);
    } catch (error) {
      alert(
        `Could not update order: ${error.message || "Network request failed."}`
      );
    }
  }
  async function cancelRequestedOrder(order) {
    if (!supabase) return alert("Connect Supabase to update cloud orders");
    if (role !== "buyer" || order.status !== "requested") return;
    if (
      !window.confirm(
        "Cancel this item request? The traveler has not accepted it yet."
      )
    )
      return;
    const { data, error } = await supabase
      .from("orders")
      .update({ status: "cancelled" })
      .eq("id", order.id)
      .eq("status", "requested")
      .select("id")
      .maybeSingle();
    if (error) return alert(error.message);
    if (!data) {
      await reload();
      return alert("This request has changed and can no longer be cancelled.");
    }
    await reload();
    alert("Request cancelled");
  }
  async function declineRequestedOrder(order) {
    if (!supabase) return alert("Connect Supabase to update cloud orders");
    if (
      role !== "traveler" ||
      order.traveler_id !== uid ||
      order.status !== "requested"
    )
      return;
    if (
      !window.confirm(
        "Decline this item request? The buyer will be notified, and the request cannot be reopened."
      )
    )
      return;
    const { data, error } = await supabase
      .from("orders")
      .update({ status: "cancelled" })
      .eq("id", order.id)
      .eq("traveler_id", uid)
      .eq("status", "requested")
      .select("id")
      .maybeSingle();
    if (error) return alert(`Could not decline request: ${error.message}`);
    if (!data) {
      await reload();
      return alert("This request has changed and can no longer be declined.");
    }
    await reload();
    alert("Request declined. The buyer was notified.");
  }
  async function openNotification(notification) {
    if (!(await markNotificationRead(notification))) return;
    if (notification.entity_type === "trip_inquiry") {
      setTab("profile");
      return;
    }
    if (notification.entity_type === "trip_inquiry_report") {
      setTab("admin");
      return;
    }
    if (
      notification.entity_type === "trip" ||
      notification.entity_type === "local_errand"
    ) {
      const isTrip = notification.entity_type === "trip",
        table = isTrip ? "trips" : "local_errand_offers",
        columns = isTrip ? "origin,travel_date" : "store_name,planned_date";
      const { data, error } = await supabase
        .from(table)
        .select(columns)
        .eq("id", notification.entity_id)
        .maybeSingle();
      if (error)
        return alert(`Could not open the related listing: ${error.message}`);
      if (!data)
        return alert(
          "This listing is no longer active or available to your account."
        );
      const query = isTrip ? data.origin : data.store_name,
        date = isTrip ? data.travel_date : data.planned_date,
        alreadyFiltered =
          marketSearch === query &&
          marketDate === date &&
          !orderSearch &&
          !orderStatus;
      setMarketSearch(query);
      setMarketDate(date);
      setOrderSearch("");
      setOrderStatus("");
      setTab("market");
      if (alreadyFiltered) await reload();
      return;
    }
    if (notification.entity_type !== "order" || !notification.entity_id) return;
    const { data, error } = await supabase
      .from("orders")
      .select("*")
      .eq("id", notification.entity_id)
      .maybeSingle();
    if (error)
      return alert(`Could not open the related order: ${error.message}`);
    if (!data)
      return alert("This order is no longer available to your account.");
    setSelectedOrder(data);
    setTab("chat");
  }
  async function loadMessages(orderId = selectedOrder?.id) {
    if (!supabase || !orderId) return;
    const requestId = ++messageLoadRequest.current;
    setMessagesLoading(true);
    setMessageError("");
    try {
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .eq("order_id", orderId)
        .order("created_at");
      if (requestId !== messageLoadRequest.current) return;
      if (error) {
        setMessageError(`Could not load messages: ${error.message}`);
        return;
      }
      setMessages(data || []);
    } catch (error) {
      if (requestId === messageLoadRequest.current)
        setMessageError(
          `Could not load messages: ${
            error.message || "Network request failed."
          }`
        );
    } finally {
      if (requestId === messageLoadRequest.current) setMessagesLoading(false);
    }
  }
  async function send() {
    if (!supabase || !selectedOrder || !draft.trim() || sendingMessage) return;
    if (!requireCompleteProfile("sending a chat message")) return;
    const text = draft.trim();
    setMessageError("");
    setSendingMessage(true);
    try {
      const { error } = await supabase
        .from("messages")
        .insert({ order_id: selectedOrder.id, sender_id: uid, body: text });
      if (error) {
        setMessageError(`Could not send message: ${error.message}`);
        return;
      }
      setDraft("");
    } catch (error) {
      setMessageError(
        `Could not send message: ${error.message || "Network request failed."}`
      );
    } finally {
      setSendingMessage(false);
    }
  }
  async function uploadProof(file) {
    if (!file || !selectedOrder || proofUploading) return;
    if (!supabase || !uid) {
      setProofError("Connect Supabase and sign in to upload proof.");
      return;
    }
    setProofError("");
    setProofNotice("");
    if (
      !["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(
        file.type
      )
    ) {
      setProofError("Choose a JPEG, PNG, WebP, or PDF proof file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setProofError("Proof files must be 10 MB or smaller.");
      return;
    }
    const path = `${uid}/${
      selectedOrder.id
    }/${Date.now()}-${crypto.randomUUID()}-${file.name.replace(
      /[^a-zA-Z0-9._-]/g,
      "_"
    )}`;
    setProofUploading(true);
    try {
      const { error: uploadError } = await supabase.storage
        .from("order-proofs")
        .upload(path, file, { upsert: false });
      if (uploadError) {
        setProofError(`Could not upload proof: ${uploadError.message}`);
        return;
      }
      const { error: recordError } = await supabase
        .from("order_proofs")
        .insert({
          order_id: selectedOrder.id,
          uploader_id: uid,
          object_path: path,
          proof_type: "receipt",
        });
      if (recordError) {
        try {
          const { error: cleanupError } = await supabase.storage
            .from("order-proofs")
            .remove([path]);
          setProofError(
            cleanupError
              ? `Proof record could not be saved: ${recordError.message}. Cleanup also failed: ${cleanupError.message}; the file may remain in private storage.`
              : `Proof record could not be saved: ${recordError.message}. The uploaded file was removed; retry the upload.`
          );
        } catch (cleanupError) {
          setProofError(
            `Proof record could not be saved: ${
              recordError.message
            }. Cleanup also failed: ${
              cleanupError.message || "Network request failed."
            }; the file may remain in private storage.`
          );
        }
        return;
      }
      setProofNotice("Proof uploaded securely.");
    } catch (error) {
      setProofError(
        `Could not complete proof upload: ${
          error.message || "Network request failed."
        }`
      );
    } finally {
      setProofUploading(false);
    }
  }
  useEffect(() => {
    if (!supabase || !selectedOrder) {
      setCashHandoff(null);
      setCashHandoffError("");
      return;
    }
    let active = true,
      channel;
    setCashHandoff(null);
    setCashHandoffError("");
    getCashHandoff(selectedOrder.id)
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          setCashHandoffError(error.message);
          return;
        }
        setCashHandoff(data || null);
      })
      .catch((error) => {
        if (active)
          setCashHandoffError(error.message || "Network request failed.");
      });
    channel = subscribeToCashHandoff(selectedOrder.id, (p) => {
      if (active) setCashHandoff(p.new);
    });
    return () => {
      active = false;
      if (channel) supabase.removeChannel(channel);
    };
  }, [selectedOrder?.id, cashHandoffReload]);
  const inspectionComplete = Object.values(inspection).every(Boolean);
  function openCashSchedule() {
    if (!requireCompleteProfile("scheduling a cash meetup")) return;
    setCashForm({
      place: cashHandoff?.meeting_place || "",
      time: toManilaDateTimeInput(cashHandoff?.meeting_time),
      landmark: "",
    });
    setDialogErrors({});
    setCashModal(true);
  }
  async function saveCashSchedule() {
    if (!requireCompleteProfile("scheduling a cash meetup")) return;
    const details = (
      cashForm.landmark
        ? `${cashForm.place.trim()} - ${cashForm.landmark.trim()}`
        : cashForm.place
    ).trim();
    const meetAt = manilaDateTimeToUtc(cashForm.time);
    const errors = {};
    if (!details) errors.place = "Enter a public meeting place.";
    else if (details.length > 300)
      errors.place =
        "Keep the meeting place and landmark to 300 characters or fewer.";
    if (
      !cashForm.time ||
      !Number.isFinite(meetAt.getTime()) ||
      meetAt.getTime() <= Date.now()
    )
      errors.time = "Choose a future date and time in Philippines time.";
    if (Object.keys(errors).length) {
      setDialogErrors(errors);
      requestAnimationFrame(() =>
        document
          .querySelector(`[name="${errors.place ? "place" : "time"}"]`)
          ?.focus()
      );
      return;
    }
    setCashBusy(true);
    try {
      const { data, error } = await scheduleCashHandoff(
        selectedOrder.id,
        details,
        meetAt.toISOString()
      );
      if (error) throw error;
      setCashHandoff(Array.isArray(data) ? data[0] : data);
      setCashModal(false);
      alert(
        `Cash meetup ${
          cashHandoff ? "rescheduled" : "scheduled"
        }. Both participants will be notified.`
      );
    } catch (error) {
      alert(
        `Could not save the meetup schedule: ${
          error.message || "Network request failed."
        }`
      );
    } finally {
      setCashBusy(false);
    }
  }
  async function confirmCash() {
    if (!requireCompleteProfile("confirming a cash handoff")) return;
    if (role === "buyer" && !inspectionComplete)
      return alert("Complete the inspection checklist first");
    setCashBusy(true);
    const { data, error } = await confirmCashReceived(selectedOrder.id);
    setCashBusy(false);
    if (error) return alert(error.message);
    setCashHandoff(Array.isArray(data) ? data[0] : data);
    alert(
      role === "buyer"
        ? "Item receipt and cash payment confirmed"
        : "Delivery and cash receipt confirmed"
    );
  }
  function openReviews(profileId) {
    setViewedProfileId(profileId === uid ? null : profileId);
    setTab("profile");
  }
  function reputationSummary(profileId) {
    if (!supabase)
      return "Transaction-verified reviews are available for completed orders.";
    if (reputationError) return "Reputation is temporarily unavailable.";
    const reputation = reputations[profileId];
    if (!reputation) return "Loading reputation…";
    const completed = Number(reputation.completed_transaction_count || 0),
      count = Number(reputation.review_count || 0);
    return count
      ? `${Number(reputation.average_rating).toFixed(1)} / 5 · ${count} review${
          count === 1 ? "" : "s"
        } · ${completed} completed transaction${completed === 1 ? "" : "s"}`
      : `No reviews yet · ${completed} completed transaction${
          completed === 1 ? "" : "s"
        }`;
  }
  const displayedProfile = viewedProfileId ? reviewProfile : profile;
  if (route === "loading")
    return (
      <main className="route-wait" aria-live="polite">
        Loading SabayBuy…
      </main>
    );
  if (route === "guard")
    return (
      <main className="route-wait" aria-live="polite">
        Redirecting to sign in…
      </main>
    );
  if (route === "redirect" || route === "not-found")
    return (
      <main className="route-wait" aria-live="polite">
        Redirecting…
      </main>
    );
  if (route === "landing") return <Landing />;
  if (route === "public-profile")
    return (
      <Suspense
        fallback={
          <main className="route-wait" role="status">
            Loading public profile…
          </main>
        }
      >
        <PublicProfilePage
          profileId={path.slice("/p/".length)}
          userId={uid}
          role={profile?.role}
          currentUserProfile={profile}
          onOpenProfile={openProfileSettings}
        />
      </Suspense>
    );
  if (route === "recovery")
    return (
      <div className="auth-page-shell">
      <main className="auth">
        <form
          className="panel"
          onSubmit={(e) => {
            e.preventDefault();
            updatePassword();
          }}
        >
          <h2>Set a new password</h2>
          <label htmlFor="new-password">New password</label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            minLength="8"
            required
            aria-describedby={`new-password-help${
              authFeedback?.type === "error" ? " auth-feedback" : ""
            }`}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setAuthFeedback(null);
            }}
          />
          <small id="new-password-help">Use at least 8 characters.</small>
          {authFeedback && (
            <p
              id="auth-feedback"
              className={
                authFeedback.type === "error"
                  ? "auth-feedback is-error"
                  : "auth-feedback is-success"
              }
              role={authFeedback.type === "error" ? "alert" : "status"}
            >
              {authFeedback.text}
            </p>
          )}
          <button type="submit" disabled={authBusy}>
            {authBusy ? "Updating password…" : "Update password"}
          </button>
        </form>
      </main>
      <SiteFooter />
      </div>
    );
  if (route === "auth")
    return (
      <div className="auth-page-shell">
      <main className="auth">
        <section className="brand" aria-label="SabayBuy overview">
          <div className="brand-badge">
            <ShoppingBag size={16} />
            Trusted marketplace
          </div>
          <div className="brand-lockup">
            <ShoppingBag size={42} />
            <h1>SabayBuy</h1>
          </div>
          <p>May biyahe sila. May pasabuy ka.</p>
          <ul className="brand-points">
            <li>Verified traveler offers</li>
            <li>Secure direct meetups</li>
            <li>Simple local buying flow</li>
          </ul>
        </section>
        <form onSubmit={auth} className="panel" aria-labelledby="auth-title">
          <h2 id="auth-title">
            {authMode === "signin" ? "Welcome back" : "Create your account"}
          </h2>
          {!configured && (
            <div className="warn">
              <AlertTriangle size={18} /> Demo mode. Add Supabase environment
              variables for cloud features.
            </div>
          )}
          <button
            type="button"
            className="google-signin"
            disabled={authBusy || !configured}
            onClick={signInWithGoogle}
          >
            {authBusy ? "Connecting…" : "Continue with Google"}
          </button>
          <button
            type="button"
            className="facebook-signin"
            disabled={authBusy || !configured}
            onClick={signInWithFacebook}
          >
            {authBusy ? "Connecting…" : "Continue with Facebook"}
          </button>
          <p className="auth-divider">or use your email</p>
          <label htmlFor="auth-email">Email</label>
          <input
            ref={authEmailRef}
            id="auth-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            aria-describedby={
              authFeedback?.type === "error" ? "auth-feedback" : undefined
            }
            onChange={(e) => {
              setEmail(e.target.value);
              setAuthFeedback(null);
            }}
          />
          <label htmlFor="auth-password">Password</label>
          <input
            id="auth-password"
            type="password"
            autoComplete={
              authMode === "signup" ? "new-password" : "current-password"
            }
            required
            minLength="6"
            aria-describedby={
              [
                authMode === "signup" ? "auth-password-help" : null,
                authFeedback?.type === "error" ? "auth-feedback" : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setAuthFeedback(null);
            }}
          />
          {authMode === "signup" && (
            <small id="auth-password-help">Use at least 6 characters.</small>
          )}
          {authFeedback && (
            <p
              id="auth-feedback"
              className={
                authFeedback.type === "error"
                  ? "auth-feedback is-error"
                  : "auth-feedback is-success"
              }
              role={authFeedback.type === "error" ? "alert" : "status"}
            >
              {authFeedback.text}
            </p>
          )}
          <button type="submit" disabled={authBusy}>
            {authBusy
              ? authMode === "signin"
                ? "Signing in…"
                : "Creating account…"
              : authMode === "signin"
              ? "Sign in"
              : "Create account"}
          </button>
          <button
            type="button"
            className="link"
            disabled={authBusy}
            onClick={() => {
              setAuthMode(authMode === "signin" ? "signup" : "signin");
              setAuthFeedback(null);
            }}
          >
            {authMode === "signin"
              ? "Create an account"
              : "Already registered?"}
          </button>
          <button
            type="button"
            className="link"
            disabled={authBusy}
            onClick={forgot}
          >
            {authBusy ? "Please wait…" : "Forgot password?"}
          </button>
        </form>
      </main>
      <SiteFooter />
      </div>
    );
  return (
    <div className="app-shell">
      <header>
        <a href="/" id="logo-link">
          <div className="logo">
            <ShoppingBag />
            SabayBuy
          </div>
        </a>
        <nav aria-label="Main navigation">
          {[
            ["market", Plane, "Trips"],
            ["orders", Package, "Orders"],
            ["chat", MessageCircle, "Chat"],
            ["profile", User, "Profile"],
            ...(role === "admin" ? [["admin", ShieldAlert, "Admin"]] : []),
          ].map(([id, I, l]) => (
            <button
              type="button"
              aria-current={tab === id ? "page" : undefined}
              className={tab === id ? "active" : ""}
              onClick={() => setTab(id)}
              key={id}
            >
              <I size={17} />
              {l}
            </button>
          ))}
          <button
            type="button"
            className={`notification-nav-button ${
              tab === "notifications" ? "active" : ""
            }`}
            aria-current={tab === "notifications" ? "page" : undefined}
            aria-label={`Notifications${
              unreadNotificationCount
                ? `, ${unreadNotificationCount} unread`
                : ""
            }`}
            onClick={() => setTab("notifications")}
          >
            <Bell size={17} />
            Notifications
            {unreadNotificationCount > 0 && (
              <span className="notification-badge" aria-hidden="true">
                {unreadNotificationLabel}
              </span>
            )}
          </button>
        </nav>
        <span className="header-welcome">
          Welcome,{" "}
          {profile?.display_name?.trim() ||
            session?.user?.email?.split("@")[0] ||
            "SabayBuy member"}
        </span>
        <button
          type="button"
          className="icon"
          aria-label="Sign out"
          onClick={() => {
            if (supabase) supabase.auth.signOut();
            else {
              setSession(null);
              navigate("/auth", true);
            }
          }}
        >
          <LogOut size={18} />
          <span>Sign out</span>
        </button>
      </header>
      <main className="content">
        {tab === "market" && (
          <MarketplaceListings
            role={role}
            userId={uid}
            marketplaceToday={marketplaceToday}
            search={marketSearch}
            onSearchChange={setMarketSearch}
            date={marketDate}
            onDateChange={setMarketDate}
            hasFilter={hasMarketplaceFilter}
            onClearFilters={clearMarketplaceFilters}
            loaded={marketplaceHasLoaded.current}
            trips={trips}
            filteredTrips={filteredTrips}
            tripsLoading={tripsLoading}
            tripsError={tripsError}
            onRetryTrips={() => reload()}
            tripsMoreError={tripsMoreError}
            tripsHasMore={tripsHasMore}
            tripsMoreLoading={tripsMoreLoading}
            onLoadMoreTrips={() => loadMoreMarketplace("trips")}
            offers={offers}
            filteredOffers={filteredOffers}
            offersLoading={offersLoading}
            offersError={offersError}
            onRetryOffers={() => reload()}
            offersMoreError={offersMoreError}
            offersHasMore={offersHasMore}
            offersMoreLoading={offersMoreLoading}
            onLoadMoreOffers={() => loadMoreMarketplace("offers")}
            ownerProfiles={ownerProfiles}
            ownerProfilesError={ownerProfilesError}
            onRetryOwnerProfiles={() =>
              setMarketProfileRefresh((value) => value + 1)
            }
            reputationSummary={reputationSummary}
            onPublishTrip={() => setModal("trip")}
            onPublishErrand={() => setModal("errand")}
            onRequestTrip={(trip) => {
              setSelectedTrip(trip);
              setSelectedOffer(null);
              setModal("request");
            }}
            onRequestOffer={(offer) => {
              setSelectedOffer(offer);
              setSelectedTrip(null);
              setModal("request");
            }}
            onDeactivateTrip={deactivateTrip}
            onDeactivateOffer={deactivateOffer}
            onOpenReviews={supabase ? openReviews : undefined}
          />
        )}
        {tab === "orders" && (
          <OrdersSection
            role={role}
            userId={uid}
            orders={orders}
            filteredOrders={filteredOrders}
            search={orderSearch}
            onSearchChange={setOrderSearch}
            status={orderStatus}
            onStatusChange={setOrderStatus}
            hasFilter={hasOrderFilter}
            onClearFilters={clearOrderFilters}
            disputes={disputes}
            disputesLoaded={disputesLoaded}
            disputesError={disputesError}
            onRetryDisputes={loadDisputes}
            buyerProfiles={orderBuyerProfiles}
            buyerProfilesLoading={orderBuyerProfilesLoading}
            buyerProfilesError={orderBuyerProfilesError}
            onRetryBuyerProfiles={() => reload()}
            travelerProfiles={orderTravelerProfiles}
            travelerProfilesLoading={orderTravelerProfilesLoading}
            travelerProfilesError={orderTravelerProfilesError}
            onRetryTravelerProfiles={() => reload()}
            loading={ordersLoading}
            loaded={marketplaceHasLoaded.current}
            error={ordersError}
            onRetryOrders={() => reload()}
            hasMore={ordersHasMore}
            moreLoading={ordersMoreLoading}
            moreError={ordersMoreError}
            onLoadMore={() => loadMoreMarketplace("orders")}
            statusLabels={statusLabels}
            canReviewParticipant={Boolean(supabase)}
            onOpenReviews={openReviews}
            onAuthorize={(order) =>
              patchOrder(order, { payment_state: "authorized" })
            }
            onCancel={cancelRequestedOrder}
            onDecline={declineRequestedOrder}
            onAdvance={advanceOrder}
            onReportIssue={(order) => {
              setDisputeOrder(order);
              setDisputeReason("");
              setDisputeError("");
            }}
            onOpenChat={(order) => {
              setSelectedOrder(order);
              setTab("chat");
            }}
          />
        )}
        {tab === "orders" && (
          <TransactionReviewsSection
            orders={filteredOrders}
            reviewData={orderReviewData}
            userId={uid}
            disputes={disputes}
            disputesLoaded={disputesLoaded}
            disputesError={disputesError}
            loading={orderReviewLoading}
            error={orderReviewError}
            onRetry={() => setOrderReviewRefresh((value) => value + 1)}
            onSubmitted={(orderId, review) => {
              if (review)
                setOrderReviewData((current) => ({
                  ...current,
                  [orderId]: {
                    ...current[orderId],
                    reviews: [...(current[orderId]?.reviews || []), review],
                  },
                }));
              setReputationRefresh((value) => value + 1);
              reload();
            }}
            onReportSubmitted={(orderId, report) =>
              setOrderReviewData((current) => ({
                ...current,
                [orderId]: {
                  ...current[orderId],
                  reviewReports: [
                    ...(current[orderId]?.reviewReports || []),
                    report,
                  ],
                },
              }))
            }
            onReportRetry={() =>
              setOrderReviewRefresh((value) => value + 1)
            }
          />
        )}
        {tab === "chat" && (
          <Suspense fallback={<p role="status">Loading chat section…</p>}>
            <ChatSection
              order={selectedOrder}
              role={role}
              userId={uid}
              cashHandoff={cashHandoff}
              cashHandoffError={cashHandoffError}
              onRetryCashHandoff={() =>
                setCashHandoffReload((value) => value + 1)
              }
              inspection={inspection}
              setInspection={setInspection}
              inspectionComplete={inspectionComplete}
              onSchedule={openCashSchedule}
              onConfirm={confirmCash}
              onReportProblem={() => {
                setDisputeOrder(selectedOrder);
                setDisputeReason("");
                setDisputeError("");
              }}
              canReportProblem={
                selectedOrder &&
                disputesLoaded &&
                statusLabels.includes(selectedOrder.status) &&
                !disputes[selectedOrder.id]
              }
              cashBusy={cashBusy}
              messages={messages}
              messagesLoading={messagesLoading}
              messageError={messageError}
              onRetryMessages={() => loadMessages(selectedOrder.id)}
              proofError={proofError}
              proofNotice={proofNotice}
              draft={draft}
              setDraft={setDraft}
              onSend={send}
              sendingMessage={sendingMessage}
              proofUploading={proofUploading}
              onUploadProof={uploadProof}
            />
          </Suspense>
        )}
        {tab === "profile" && (
          <ProfileSection
            name={
              viewedProfileId
                ? reviewProfile?.display_name || "SabayBuy member"
                : profile?.display_name || session.user.email
            }
            displayedProfile={displayedProfile}
            viewedProfileId={viewedProfileId}
            configured={configured}
            userId={uid}
            role={role}
            profile={profile}
            onProfileSaved={setProfile}
            onBackToProfile={() => openReviews(uid)}
            onTravelerVerificationUpdated={() => {
              setProfileReviewRefresh((value) => value + 1);
              setMarketProfileRefresh((value) => value + 1);
              reload();
            }}
          >
            <Suspense fallback={<p role="status">Loading profile reviews…</p>}>
              <ProfileReputationSection
                reputation={reputations[viewedProfileId || uid]}
                reputationError={reputationError}
                onRetryReputation={() =>
                  setReputationRefresh((value) => value + 1)
                }
                reviews={profileReviews}
                reviewAuthors={profileReviewAuthors}
                reviewsLoading={profileReviewsLoading}
                reviewsError={profileReviewsError}
                onRetryReviews={() =>
                  setProfileReviewRefresh((value) => value + 1)
                }
                userId={uid}
                reviewReports={profileReviewReports}
                reviewReportsError={profileReviewReportsError}
                reviewReportsLoading={profileReviewReportsLoading}
                onRetryReviewReports={() =>
                  setProfileReviewRefresh((value) => value + 1)
                }
                onReportSubmitted={(report) =>
                  setProfileReviewReports((current) => ({
                    ...current,
                    [report.review_id]: report,
                  }))
                }
              />
            </Suspense>
          </ProfileSection>
        )}
        {tab === "admin" &&
          (role !== "admin" ? (
            <p role="alert">Access denied. Administrator access is required.</p>
          ) : (
            <Suspense fallback={<p role="status">Loading admin review tools…</p>}>
              {DISPUTES_ENABLED && <AdminDisputeReviewPanel />}
              <AdminNoShowReportsPanel />
              <AdminReviewReportsPanel />
              <AdminTravelerRoleRequestsPanel />
              <TripInquiriesPanel userId={uid} role={role} />
            </Suspense>
          ))}
        {tab === "notifications" && (
          <NotificationInbox
            notifications={notifications}
            unreadCount={unreadNotificationCount}
            loading={notificationsLoading}
            error={notificationsError}
            busyId={notificationBusyId}
            bulkBusy={notificationBulkBusy}
            olderLoading={notificationsOlderLoading}
            olderError={notificationsOlderError}
            hasMore={notificationsHasMore}
            onMarkAllRead={markAllNotificationsRead}
            onRetry={() => loadNotifications()}
            onOpenNotification={openNotification}
            onMarkRead={markNotificationRead}
            onRetryOlder={() => loadNotifications(false, true)}
            onLoadOlder={() => loadNotifications(false, true)}
          />
        )}
        {notice && <div className="toast">{notice}</div>}
      </main>
      {DISPUTES_ENABLED && disputeOrder && (
        <DisputeDialog
          dialogRef={dialogRef}
          reason={disputeReason}
          error={disputeError}
          fieldError={dialogErrors.disputeReason}
          busy={disputeBusy}
          onClose={() => setDisputeOrder(null)}
          onReasonChange={setDisputeReason}
          onClearFieldError={clearDialogError}
          onSubmit={validateDisputeForm}
        />
      )}
      {cashModal && (
        <CashScheduleDialog
          dialogRef={dialogRef}
          handoff={cashHandoff}
          form={cashForm}
          errors={dialogErrors}
          busy={cashBusy}
          minDateTime={currentManilaDateTimeInput()}
          onClose={() => setCashModal(false)}
          onFieldChange={(field, value) =>
            setCashForm((current) => ({ ...current, [field]: value }))
          }
          onClearFieldError={clearDialogError}
          onSubmit={(event) => {
            if (validateDialogForm(event, ["place"])) saveCashSchedule();
          }}
        />
      )}
      {modal && (
        <MarketplaceActionDialog
          dialogRef={dialogRef}
          type={modal}
          errors={dialogErrors}
          newTrip={newTrip}
          newErrand={newErrand}
          request={request}
          marketplaceToday={marketplaceToday}
          selectedOffer={selectedOffer}
          selectedTrip={selectedTrip}
          onClose={() => {
            setModal(null);
            setDialogErrors({});
          }}
          onTripFieldChange={(field, value) =>
            updateListingField("trip", field, value)
          }
          onErrandFieldChange={(field, value) =>
            updateListingField("errand", field, value)
          }
          onRequestFieldChange={updateRequestField}
          onSubmitTrip={(event) => {
            if (validateDialogForm(event, ["origin", "destination"]))
              createTrip();
          }}
          onSubmitErrand={(event) => {
            if (validateDialogForm(event, ["area", "store"])) createErrand();
          }}
          onSubmitRequest={(event) => {
            if (validateDialogForm(event, ["item", "city"])) createOrder();
          }}
        />
      )}
      <SiteFooter />
    </div>
  );
}
